import { memo, useCallback, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Virtuoso } from "react-virtuoso";
import { Search, X, Check, Forward, User } from "lucide-react";

const ContactItem = memo(({ contact, selected, toggleContact }) => {
  return (
    <button
      onClick={() => toggleContact(contact)}
      className="flex w-full items-center gap-4 border-b border-gray-100 p-4 transition hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800"
    >
      <div className="h-12 w-12 overflow-hidden rounded-full bg-gray-200">
        {contact.profilePic ? (
          <img
            src={contact.profilePic}
            alt={contact.name}
            loading="lazy"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/default-avatar.png";
            }}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <User className="h-6 w-6 text-gray-500" />
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1 text-left">
        <p className="truncate font-medium text-gray-900 dark:text-white">
          {contact.name || "Sem nome"}
        </p>

        <p className="truncate text-sm text-gray-500">{contact.phoneNumber}</p>
      </div>

      <div
        className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border transition ${
          selected
            ? "border-green-500 bg-green-500 text-white"
            : "border-gray-400"
        }`}
      >
        {selected && <Check className="h-4 w-4" />}
      </div>
    </button>
  );
});

const ForwardMessageModal = ({ contacts, onClose, onForward }) => {
  const modalRef = useRef(null);

  const [search, setSearch] = useState("");
  const [selectedContacts, setSelectedContacts] = useState([]);

  const handleOverlayClick = useCallback(
    (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    },
    [onClose],
  );

  const filteredContacts = useMemo(() => {
    const text = search.toLowerCase().trim();

    if (!text) return contacts;

    return contacts.filter(
      (contact) =>
        contact.name?.toLowerCase().includes(text) ||
        contact.phoneNumber?.toLowerCase().includes(text),
    );
  }, [contacts, search]);

  const selectedIds = useMemo(
    () => new Set(selectedContacts.map((c) => c.id)),
    [selectedContacts],
  );

  const toggleContact = useCallback((contact) => {
    setSelectedContacts((prev) => {
      const exists = prev.some((c) => c.id === contact.id);

      if (exists) {
        return prev.filter((c) => c.id !== contact.id);
      }

      return [...prev, contact];
    });
  }, []);

  const handleForward = useCallback(() => {
    onForward(selectedContacts.map((c) => c.whatsappId));
    onClose();
  }, [selectedContacts, onForward, onClose]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleOverlayClick}
      >
        <motion.div
          ref={modalRef}
          className="flex h-[80vh] w-full max-w-xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
        >
          {/* Header */}

          <div className="flex items-center justify-between border-b border-gray-200 p-5 dark:border-gray-700">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                <Forward className="h-5 w-5" />
                Encaminhar mensagem
              </h2>

              {selectedContacts.length > 0 && (
                <p className="mt-1 text-sm text-gray-500">
                  {selectedContacts.length} contato
                  {selectedContacts.length > 1 ? "s" : ""} selecionado
                  {selectedContacts.length > 1 ? "s" : ""}
                </p>
              )}
            </div>

            <button
              onClick={onClose}
              className="rounded-lg p-2 transition hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Busca */}

          <div className="border-b border-gray-200 p-4 dark:border-gray-700">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />

              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Pesquisar contato..."
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-3 outline-none focus:border-green-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>
          </div>

          {/* Lista */}

          <div className="flex-1">
            {filteredContacts.length === 0 ? (
              <div className="flex h-full items-center justify-center text-gray-500">
                Nenhum contato encontrado.
              </div>
            ) : (
              <Virtuoso
                className="h-full"
                data={filteredContacts}
                increaseViewportBy={500}
                itemContent={(index, contact) => (
                  <ContactItem
                    contact={contact}
                    selected={selectedIds.has(contact.id)}
                    toggleContact={toggleContact}
                  />
                )}
              />
            )}
          </div>

          {/* Footer */}

          <div className="border-t border-gray-200 p-4 dark:border-gray-700">
            <button
              disabled={selectedContacts.length === 0}
              onClick={handleForward}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-600 py-3 font-medium text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Forward className="h-5 w-5" />
              Encaminhar
              {selectedContacts.length > 0 && ` (${selectedContacts.length})`}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default memo(ForwardMessageModal);
